import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  ReactiveFormsModule,
} from '@angular/forms';
import { AnswerModuleService } from '@core/services/answer-module.service';
import { ConfigService } from '@core/services/config.service';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { FileUploadComponent } from '@questions/file-upload/file-upload.component';
import { HierarchicalComponent } from '@questions/hierarchical/hierarchical.component';
import { InformationTextComponent } from '@questions/information-text/information-text.component';
import { LocaleDateComponent } from '@questions/locale-date/locale-date.component';
import { MatrixComponent } from '@questions/matrix/matrix.component';
import { MultiMatrixComponent } from '@questions/multi-matrix/multi-matrix.component';
import { MultiSelectComponent } from '@questions/multi-select/multi-select.component';
import { OpenAnswerComponent } from '@questions/open-answer/open-answer.component';
import { PriorityDragdropComponent } from '@questions/priority-dragdrop/priority-dragdrop.component';
import { PriorityDropdownComponent } from '@questions/priority-dropdown/priority-dropdown.component';
import { NpsComponent } from '@questions/single-select-questions/nps/nps.component';
import { SatisfactionMeterComponent } from '@questions/single-select-questions/satisfaction-meter/satisfaction-meter.component';
import { SingleSelectDropdownComponent } from '@questions/single-select-questions/single-select-dropdown/single-select-dropdown.component';
import { SingleSelectComponent } from '@questions/single-select-questions/single-select/single-select.component';
import { ValidationErrorType } from '@shared/models/enums/backend-error-type.enum';
import { PreviewMode } from '@shared/models/enums/preview-mode.enum';
import { PageProperties } from '@shared/models/page-properties';
import {
  QuestionAnswerRequest,
  ResetResponseData,
} from '@shared/models/question-answer-request';
import { QuestionAnswerResponseData } from '@shared/models/question-answer-response';
import { ValidationError } from '@shared/models/validation-error';
import { Question } from '@shared/models/views-models/question-view';
import { SafeHtmlPipe } from '@shared/pipes/safe-html.pipe';
import { ClearErrorsByQuestionId, ErrorState } from '@shared/state/error.state';
import { createForm } from '@shared/utils/form-builder.util';
import {
  findQuestionById,
  getFormGroup,
  validateAllFormFields,
} from '@shared/utils/form.util';
import { isObject } from '@shared/utils/helper.util';
import {
  getQuestionAnswers,
  handleQuestionAnswer,
} from '@shared/utils/question-answer.util';
import {
  handleValidationErrors,
  maxLengthValidator,
} from '@shared/utils/validators.util';
import { debounceTime, firstValueFrom } from 'rxjs';
import { SurveySkeletonComponent } from '../survey-skeleton/survey-skeleton.component';

@Component({
  selector: 'app-survey-form',
  templateUrl: './survey-form.component.html',
  styleUrls: ['./survey-form.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    SafeHtmlPipe,
    OpenAnswerComponent,
    MatrixComponent,
    MultiMatrixComponent,
    SingleSelectDropdownComponent,
    SatisfactionMeterComponent,
    NpsComponent,
    SingleSelectComponent,
    InformationTextComponent,
    HierarchicalComponent,
    PriorityDropdownComponent,
    MultiSelectComponent,
    LocaleDateComponent,
    FileUploadComponent,
    PriorityDragdropComponent,
    SurveySkeletonComponent,
  ],
})
export class SurveyFormComponent implements OnChanges, AfterViewInit, OnInit {
  @ViewChild(FormGroupDirective) formGroupDirective!: FormGroupDirective;
  @Input({ required: true }) responseId!: string;
  @Input({ required: true }) currentState!: string;
  @Input() questions: Question[] = [];
  @Input() answers: QuestionAnswerResponseData[] = [];
  @Input() previewMode?: string;
  @Input({ required: true }) displayBackButton!: boolean | undefined;
  @Input() showNavigation = true;
  @Input({ required: true }) surveyTitle!: string;
  @Input() loading?: boolean;
  @Output() submitEmitter: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() previousEmitter: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() formReady = new EventEmitter<FormGroup>();
  @Output() deactivateGuard = new EventEmitter<boolean>();
  @Output() validationErrorCountEmitter = new EventEmitter<null>();
  @Output() pagePropertiesEmitter: EventEmitter<PageProperties> =
    new EventEmitter<PageProperties>();

  form: FormGroup = new FormGroup({});
  isLoading = true;
  getFormGroup = getFormGroup;
  private lastAnswerPromise = Promise.resolve();

  get showSkeleton(): boolean {
    return this.loading ?? this.isLoading;
  }

  hasDropdownRow(question: Question): boolean {
    if (question.type !== 'MULTI_MATRIX') return false;
    return (
      (question as any).columns?.[0]?.rows?.some(
        (q: any) => q?.presentation === 'DROPDOWN',
      ) ?? false
    );
  }

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private store: Store,
    private answerModuleService: AnswerModuleService,
    private configService: ConfigService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['questions'] &&
      changes['questions'].currentValue !== changes['questions'].previousValue
    ) {
      const newForm = createForm(
        this.fb,
        this.questions,
        this.answers,
        this.configService.get('USE_CLIENT_VALIDATION'),
      );
      if (newForm) {
        this.form = newForm;
        Promise.resolve().then(() => {
          if (
            this.form &&
            this.previewMode != PreviewMode.FreeFlow &&
            this.currentState !== '00000000-0000-0000-0000-000000000000'
          ) {
            this.initializeFormValueChanges();
          }
        });
      }
      this.cdr.detectChanges();
    }
  }

  ngOnInit() {
    Promise.resolve().then(() => {
      this.formReady.emit(this.form);
      this.store.select(ErrorState.getErrors).subscribe((errors) => {
        this.handleBackendErrors(errors);
      });
    });
  }

  ngAfterViewInit() {
    Promise.resolve().then(() => {
      this.isLoading = false;
      if (
        this.form &&
        this.previewMode != PreviewMode.FreeFlow &&
        this.currentState !== '00000000-0000-0000-0000-000000000000'
      ) {
        this.revalidateFormOnInit();
      }
    });
  }

  handleBackendErrors(errors: ValidationError[]): void {
    const currentErrorQuestionIds = new Set<string>();
    Object.keys(this.form.controls).forEach((key) => {
      const control = this.form.get(key);
      if (control?.errors) {
        const hasBackendError = Object.keys(control.errors).some((errorKey) =>
          Object.values(ValidationErrorType).includes(
            errorKey as ValidationErrorType,
          ),
        );
        if (hasBackendError) {
          currentErrorQuestionIds.add(key.replace('-OTHER', ''));
        }
      }
    });

    const newErrorQuestionIds = new Set(errors.map((e) => e.questionId));

    currentErrorQuestionIds.forEach((questionId) => {
      if (!newErrorQuestionIds.has(questionId)) {
        this.clearBackendValidatorsForQuestion(questionId);
      }
    });

    // Group errors by questionId
    const errorsByQuestion = new Map<string, ValidationError[]>();
    errors.forEach((error) => {
      const existing = errorsByQuestion.get(error.questionId) || [];
      existing.push(error);
      errorsByQuestion.set(error.questionId, existing);
    });

    // Apply all validators for each question
    errorsByQuestion.forEach((questionErrors, questionId) => {
      const control = this.form.get(questionId);
      const otherControl = this.form.get(`${questionId}-OTHER`);

      if (control) {
        this.setControllers(control, questionErrors);
        this.cdr.markForCheck();
      }

      if (otherControl) {
        otherControl.setValidators(maxLengthValidator(255, 'characters'));
      }
    });

    this.cdr.detectChanges();
    setTimeout(() => {
      this.emitValidationErrorCount();
    }, 0);
  }

  setControllers(
    control: AbstractControl<any, any> | FormGroup<any>,
    errors: ValidationError[],
  ): void {
    if (errors.length === 0) return;

    const question = findQuestionById(errors[0].questionId, this.questions);
    if (!question) return;

    // MULTIPLE_CHOICE (checkbox groups) - apply errors to individual checkboxes
    if (question.type === 'MULTIPLE_CHOICE' && control instanceof FormGroup) {
      this.applyMultipleChoiceErrors(control, question, errors);
      return;
    }

    // HIERARCHICAL - apply errors to all level controls
    if (question.type === 'HIERARCHICAL' && control instanceof FormGroup) {
      this.applyHierarchicalErrors(control, question, errors);
      return;
    }

    // all other question types
    this.applyStandardErrors(control, question, errors);
  }

  private applyMultipleChoiceErrors(
    formGroup: FormGroup,
    question: Question,
    errors: ValidationError[],
  ): void {
    const formGroupValidators = errors
      .map((error) => handleValidationErrors(question, error))
      .filter((v) => v !== null);

    if (formGroupValidators.length > 0) {
      formGroup.setValidators(formGroupValidators);
      formGroup.updateValueAndValidity();
      formGroup.markAsTouched();
      formGroup.markAsDirty();
    }

    errors.forEach((error) => {
      if (!error.alternativeId) return;

      const checkboxControl = formGroup.get(error.alternativeId);
      if (!checkboxControl) return;

      const validator = handleValidationErrors(question, error);
      if (!validator) return;

      const existingValidators = checkboxControl.validator
        ? [checkboxControl.validator]
        : [];

      checkboxControl.setValidators([...existingValidators, validator]);
      checkboxControl.updateValueAndValidity();
      checkboxControl.markAsTouched();
      checkboxControl.markAsDirty();
    });
  }

  private applyHierarchicalErrors(
    formGroup: FormGroup,
    question: Question,
    errors: ValidationError[],
  ): void {
    const validators = errors
      .map((error) => handleValidationErrors(question, error))
      .filter((v) => v !== null);

    if (validators.length === 0) return;

    // Apply same validators to all hierarchical level controls
    Object.values(formGroup.controls).forEach((ctrl: AbstractControl) => {
      ctrl.setValidators(validators);
      ctrl.updateValueAndValidity();
    });

    formGroup.updateValueAndValidity();
    formGroup.markAsTouched();
    formGroup.markAsDirty();
  }

  private applyStandardErrors(
    control: AbstractControl,
    question: Question,
    errors: ValidationError[],
  ): void {
    const validators = errors
      .map((error) => handleValidationErrors(question, error))
      .filter((v) => v !== null);

    if (validators.length === 0) return;

    control.setValidators(validators);
    control.updateValueAndValidity();
    control.markAsTouched();
    control.markAsDirty();
  }

  private revalidateFormOnInit(): void {
    setTimeout(() => {
      Object.keys(this.form.controls).forEach((questionId) => {
        const control = this.form.get(questionId);
        if (!control || !control.value) return;

        const question = this.questions.find((q) => q.id === questionId);
        if (!question) return;

        this.postAnswer(question, question.id);
      });
    }, 100);
  }

  private initializeFormValueChanges() {
    let previousValues = { ...this.form.value };

    this.form.valueChanges.pipe(debounceTime(500)).subscribe((currentValue) => {
      const changedIds = this.getChangedQuestionIds(
        currentValue,
        previousValues,
      );
      if (changedIds?.length) {
        changedIds.forEach((questionId) => {
          const question = this.questions.find((q) =>
            this.isMatchingParentQuestion(q, questionId),
          );
          if (question) {
            this.postAnswer(question, questionId);
          }
        });
      }
      previousValues = { ...currentValue };
      this.emitValidationErrorCount();
    });
  }

  private getChangedQuestionIds(
    current: any,
    previous: any,
  ): string[] | undefined {
    const changedPaths = this.findChangedKeys(current, previous);
    if (!changedPaths) return undefined;
    return Array.from(
      new Set(
        changedPaths.map((path) =>
          (path.split('.').pop() || '').replace('-OTHER', ''),
        ),
      ),
    ).filter((value): value is string => Boolean(value));
  }

  private findChangedKeys(current: any, old: any, parentKey = ''): string[] {
    const changedKeys: string[] = [];
    for (const [key, value] of Object.entries(current)) {
      const fullKey = parentKey ? `${parentKey}.${key}` : key;
      const currentValue = value;
      const oldValue = old[key];

      if (fullKey.includes('_')) {
        const baseId = fullKey.split('.')[0];
        if (!changedKeys.includes(baseId)) {
          changedKeys.push(baseId);
        }
        continue;
      }

      if (
        isObject(currentValue) &&
        !Array.isArray(currentValue) &&
        !this.isEqual(currentValue, oldValue)
      ) {
        changedKeys.push(
          ...this.findChangedKeys(currentValue, oldValue, fullKey),
        );
      } else if (!this.isEqual(currentValue, oldValue)) {
        changedKeys.push(fullKey);
      }
    }
    return [...new Set(changedKeys)];
  }

  isEqual(value1: any, value2: any): boolean {
    if (value1 === value2) return true;
    if (typeof value1 !== typeof value2) return false;
    if (typeof value1 !== 'object') return false;
    if (!value1 || !value2) return false;
    const keys1 = Object.keys(value1);
    const keys2 = Object.keys(value2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => this.isEqual(value1[key], value2[key]));
  }

  private postAnswer(question: Question, changedId: string) {
    this.lastAnswerPromise = this.lastAnswerPromise.then(async () => {
      const answersToPost = handleQuestionAnswer(
        this.form,
        question,
        this.responseId,
      ).filter((answer) =>
        this.filterNonMatchingChangeId(answer, question, changedId),
      );
      for (const answer of answersToPost) {
        try {
          await firstValueFrom(
            this.answerModuleService.postAnswer(
              this.responseId,
              this.currentState,
              answer,
            ),
          )
            .catch((error) => {
              // to prevent flicker, clear here and not before the call
              this.store.dispatch(new ClearErrorsByQuestionId(question.id));
              this.clearBackendValidatorsForQuestion(question.id);
              this.deactivateGuard.emit(true);
              this.answerModuleService.handleError(error);
            })
            .then((response) => {
              if (response?.body) {
                // to prevent flicker, clear here and not before the call
                this.store.dispatch(new ClearErrorsByQuestionId(question.id));
                this.clearBackendValidatorsForQuestion(question.id);
                this.pagePropertiesEmitter.emit(response.body);
              }
            });
        } catch (error) {
          void error;
        }
      }
    });
  }

  private filterNonMatchingChangeId(
    answer: QuestionAnswerRequest,
    question: Question,
    changedId: string,
  ): boolean {
    switch (answer.data.$type) {
      case 'multiSelectResponseData':
        if (question.type === 'MULTIPLE_CHOICE') {
          return (
            question.alternatives.some((i) => i.id === changedId) ||
            question.id === changedId
          );
        } else if (question.type === 'MATRIX') {
          const row = question.rows.find(
            (i) => i.id === answer.data.questionId,
          );
          if (row) {
            return this.filterNonMatchingChangeId(answer, row, changedId);
          }
        } else if (question.type === 'MULTI_MATRIX') {
          const row = question.columns.find((i) =>
            i.rows.find((j) => j.id === answer.data.questionId),
          );
          if (row) {
            return this.filterNonMatchingChangeId(answer, row, changedId);
          }
        }
        return false;
      case 'priorityResponseDropData':
        if (question.type === 'PRIORITY_DROPDOWN') {
          return answer.data.questionId === changedId;
        }
        return false;
      case 'hierarchicalResponseData':
        if (question.type === 'HIERARCHICAL') {
          return question.levels.some(
            (level) => level.id === answer.data.questionId,
          );
        }
        return false;
      case 'resetResponseData':
        return this.filterResetData(question, answer.data, changedId);
      default:
        return answer.data.questionId === changedId;
    }
  }

  private filterResetData(
    question: Question,
    resetData: ResetResponseData,
    changeId: string,
  ): boolean {
    switch (question.type) {
      case 'MATRIX':
        return question.rows.some((row) => {
          return this.filterResetData(row, resetData, changeId);
        });
      case 'MULTIPLE_CHOICE':
        return resetData.questionId === question.id;
      case 'SINGLE_CHOICE':
        return (
          resetData.questionId ===
            question.alternatives.find((alt) => alt.id === changeId)?.id ||
          resetData.questionId === changeId
        );
      case 'HIERARCHICAL':
        return true;
      default:
        return resetData.questionId === changeId;
    }
  }

  private isMatchingParentQuestion(
    question: Question,
    questionId: string,
  ): boolean {
    switch (question.type) {
      case 'MULTIPLE_CHOICE':
      case 'SINGLE_CHOICE':
        return (
          question.alternatives.some((alt) => alt.id === questionId) ||
          question.id === questionId
        );
      case 'MATRIX':
        return (
          question.rows.some((row) =>
            this.isMatchingParentQuestion(row, questionId),
          ) || question.id === questionId
        );
      case 'PRIORITY_DROPDOWN':
        return (
          question.rows.some((row) => row.id === questionId) ||
          question.id === questionId
        );
      case 'MULTI_MATRIX':
        return (
          question.columns.some((column) =>
            column.rows.some((row) =>
              this.isMatchingParentQuestion(row, questionId),
            ),
          ) || question.id === questionId
        );
      default:
        return question.id === questionId;
    }
  }

  submit(event: Event) {
    event.preventDefault();
    if (this.form.valid || this.previewMode === PreviewMode.FreeFlow) {
      this.form.markAsUntouched();
      const answers = getQuestionAnswers(
        this.form,
        this.questions,
        this.responseId,
      );
      this.submitEmitter.emit(answers);
    } else {
      validateAllFormFields(this.form);
      this.emitValidationErrorCount();
    }
  }

  emitValidationErrorCount() {
    this.validationErrorCountEmitter.emit();
  }

  handlePrevBtnClick(event: Event) {
    event.preventDefault();
    this.formGroupDirective.resetForm();
    this.previousEmitter.emit();
  }

  private clearBackendValidatorsForQuestion(questionId: string): void {
    const control = this.form.get(questionId);
    if (!control) return;

    // handle MULTIPLE_CHOICE
    if (control instanceof FormGroup) {
      const question = findQuestionById(questionId, this.questions);
      if (question?.type === 'MULTIPLE_CHOICE') {
        this.clearMultipleChoiceErrors(control);
      }
      if (question?.type === 'HIERARCHICAL') {
        this.clearFormGroupChildErrors(control);
      }
    }

    // Clear errors from the FormGroup
    this.clearControlErrors(control);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private clearMultipleChoiceErrors(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((alternativeId) => {
      const checkboxControl = formGroup.get(alternativeId);
      if (checkboxControl) {
        this.clearControlErrors(checkboxControl);
        checkboxControl.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  private clearFormGroupChildErrors(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((childControl) => {
      this.clearControlErrors(childControl);
      childControl.updateValueAndValidity({ emitEvent: false });
    });
  }

  private clearControlErrors(control: AbstractControl): void {
    if (!control.errors) return;

    const filteredErrors = { ...control.errors };

    // Remove all backend validation error types
    Object.values(ValidationErrorType).forEach((errorType) => {
      delete filteredErrors[errorType];
    });

    const hasRemainingErrors = Object.keys(filteredErrors).length > 0;
    control.setErrors(hasRemainingErrors ? filteredErrors : null);

    // If no errors remain, clear validators and reset touched state
    if (!hasRemainingErrors) {
      control.clearValidators();
      control.markAsUntouched();
    }
  }
}
